import { useRef, useState, type FormEvent } from 'react';
import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FileUp,
  Info,
  Loader2,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/lib/auth/store';
import { cn } from '@/lib/utils';

import { ApiErrorBanner } from './ApiErrorBanner';
import {
  useArchiveYieldCurve,
  useUploadYieldCurve,
  useValidateYieldCurve,
  useYieldCurveDetail,
  useYieldCurves,
} from './yieldCurveApi';
import type { SnapshotStatus, YieldCurveSnapshot } from './yieldCurveTypes';

/**
 * /parameters/yield-curves — Phase 2.0.NIM.v1.
 *
 * Lists all snapshots, accepts xlsx uploads (DRAFT), and exposes the
 * validate / archive actions to ALM_ADMIN. Two-eyes enforcement happens
 * server-side; the UI just hands off the action and surfaces the error
 * if it bounces.
 */
export function YieldCurvesPage() {
  const isAdmin = useAuthStore((s) => s.hasRole('ALM_ADMIN'));
  const list = useYieldCurves();
  const upload = useUploadYieldCurve();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currency, setCurrency] = useState<string>('');
  const [replace, setReplace] = useState<boolean>(false);

  function toggle(id: string) {
    const next = new Set(expanded);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpanded(next);
  }

  function handleUpload(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;
    upload.mutate(
      { file, currency: currency || undefined, replace },
      { onSuccess: () => { if (fileInputRef.current) fileInputRef.current.value = ''; } },
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Yield Curves</h1>
          <p className="text-sm text-muted-foreground">
            Upload, validate and archive yield-curve snapshots. Engines (NIM,
            Duration when it ships, IRRBB) consume the latest{' '}
            <strong>VALIDATED</strong> snapshot for the calculation date.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => list.refetch()} disabled={list.isFetching}>
          {list.isFetching ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Reload
        </Button>
      </header>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Upload curve (xlsx)</CardTitle>
          <CardDescription>
            File is parsed against the standard layout (Instrument / Tenor /
            Stamp / Maturity / Mkt.Values / Zero Cpn. / Discount / Forward 3M).
            Snapshot lands in <strong>DRAFT</strong> status — admin must
            validate before engines pick it up.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!isAdmin ? (
            <p className="rounded border bg-muted/30 p-3 text-sm text-muted-foreground">
              <Info className="mr-1.5 inline h-4 w-4" />
              ALM_ADMIN role required to upload curves.
            </p>
          ) : (
            <form onSubmit={handleUpload} className="grid gap-3 md:grid-cols-[1fr_8rem_8rem_auto]">
              <div className="space-y-1.5">
                <Label htmlFor="curve-file">Curve workbook</Label>
                <Input
                  id="curve-file"
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="curve-ccy">Currency override</Label>
                <Input
                  id="curve-ccy"
                  placeholder="(optional)"
                  maxLength={3}
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="curve-replace">Replace existing</Label>
                <select
                  id="curve-replace"
                  value={replace ? 'true' : 'false'}
                  onChange={(e) => setReplace(e.target.value === 'true')}
                  className={cn(
                    'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm',
                  )}
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </select>
              </div>
              <div className="flex items-end">
                <Button type="submit" disabled={upload.isPending}>
                  {upload.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <FileUp className="mr-2 h-4 w-4" />
                  )}
                  Upload
                </Button>
              </div>
            </form>
          )}

          {upload.isError ? <ApiErrorBanner error={upload.error} /> : null}
          {upload.isSuccess ? (
            <p className="mt-3 flex items-center gap-1.5 text-sm text-emerald-600">
              <CheckCircle2 className="h-4 w-4" /> Uploaded — snapshot{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">{upload.data.snapshotId}</code>
              {' '}is in DRAFT.
            </p>
          ) : null}
        </CardContent>
      </Card>

      {list.isError ? <ApiErrorBanner error={list.error} /> : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">All snapshots</CardTitle>
          <CardDescription>
            {list.data ? `${list.data.length} snapshot(s)` : 'Loading…'}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {list.isLoading ? (
            <p className="px-4 py-6 text-center text-muted-foreground">
              <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
            </p>
          ) : (list.data ?? []).length === 0 ? (
            <p className="px-4 py-6 text-center text-muted-foreground">
              No snapshots yet. Upload one above.
            </p>
          ) : (
            <ul className="divide-y">
              {list.data!.map((s) => (
                <SnapshotRow
                  key={s.snapshotId}
                  s={s}
                  expanded={expanded.has(s.snapshotId)}
                  onToggle={() => toggle(s.snapshotId)}
                  isAdmin={isAdmin}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SnapshotRow({
  s,
  expanded,
  onToggle,
  isAdmin,
}: {
  s: YieldCurveSnapshot;
  expanded: boolean;
  onToggle: () => void;
  isAdmin: boolean;
}) {
  const validate = useValidateYieldCurve();
  const archive  = useArchiveYieldCurve();
  const detail   = useYieldCurveDetail(expanded ? s.snapshotId : undefined);

  const tone = statusTone(s.status);

  return (
    <li>
      <div className="flex items-center gap-3 px-4 py-3">
        <Button variant="ghost" size="sm" onClick={onToggle}>
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">{s.curveId}</span>
            <span className="text-xs text-muted-foreground">@ {s.asOfDate}</span>
            <span className={cn('rounded px-1.5 py-0.5 text-[11px] font-medium', tone)}>
              {s.status}
            </span>
            <span className="rounded bg-secondary px-1.5 py-0.5 text-[11px] font-medium text-secondary-foreground">
              {s.source}
            </span>
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {s.pointCount} point(s) · uploaded by {s.createdBy ?? '—'}
            {s.validatedBy ? <> · validated by {s.validatedBy}</> : null}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {isAdmin && s.status === 'DRAFT' ? (
            <Button
              variant="ghost"
              size="sm"
              title="Validate (DRAFT → VALIDATED)"
              disabled={validate.isPending}
              onClick={() => validate.mutate(s.snapshotId)}
            >
              <ShieldCheck className="h-4 w-4 text-primary" />
            </Button>
          ) : null}
          {isAdmin && s.status !== 'ARCHIVED' ? (
            <Button
              variant="ghost"
              size="sm"
              title="Archive"
              disabled={archive.isPending}
              onClick={() => archive.mutate(s.snapshotId)}
            >
              <Archive className="h-4 w-4 text-destructive" />
            </Button>
          ) : null}
        </div>
      </div>

      {(validate.isError || archive.isError) ? (
        <div className="px-4 pb-3">
          {validate.isError ? <ApiErrorBanner error={validate.error} /> : null}
          {archive.isError ? <ApiErrorBanner error={archive.error} /> : null}
        </div>
      ) : null}

      {expanded ? (
        <div className="border-t bg-muted/20 px-4 py-3">
          {detail.isLoading ? (
            <p className="text-xs text-muted-foreground">
              <Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" /> Loading points…
            </p>
          ) : detail.data ? (
            <div className="overflow-x-auto rounded border bg-background">
              <table className="w-full text-xs">
                <thead className="border-b bg-muted/40 uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-1.5 text-left font-medium">Tenor</th>
                    <th className="px-3 py-1.5 text-right font-medium">Days</th>
                    <th className="px-3 py-1.5 text-left font-medium">Type</th>
                    <th className="px-3 py-1.5 text-left font-medium">Maturity</th>
                    <th className="px-3 py-1.5 text-right font-medium">Quoted %</th>
                    <th className="px-3 py-1.5 text-right font-medium">Zero %</th>
                    <th className="px-3 py-1.5 text-right font-medium">Discount</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.data.points ?? []).map((p) => (
                    <tr key={p.tenorCode} className="border-b last:border-0">
                      <td className="px-3 py-1 font-mono">{p.tenorCode}</td>
                      <td className="px-3 py-1 text-right text-muted-foreground tabular-nums">{p.tenorDays}</td>
                      <td className="px-3 py-1">{p.instrumentType}</td>
                      <td className="px-3 py-1 text-muted-foreground">{p.maturityDate}</td>
                      <td className="px-3 py-1 text-right tabular-nums">{p.quotedRatePct ?? '—'}</td>
                      <td className="px-3 py-1 text-right tabular-nums font-medium">{p.zeroRatePct}</td>
                      <td className="px-3 py-1 text-right tabular-nums">{p.discountFactor ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-xs text-destructive">
              <AlertTriangle className="mr-1 inline h-3.5 w-3.5" /> Failed to load points.
            </p>
          )}
        </div>
      ) : null}
    </li>
  );
}

function statusTone(status: SnapshotStatus): string {
  switch (status) {
    case 'VALIDATED': return 'bg-emerald-500/10 text-emerald-700';
    case 'DRAFT':     return 'bg-amber-500/10 text-amber-700';
    case 'ARCHIVED':  return 'bg-muted text-muted-foreground';
  }
}
